/**
 * Farm stay pricing. One place, because the quote on the page, the quote in the
 * booking form and the amount Ananya sees must never disagree.
 *
 * Current package: 3,000 per person per night, 2-4 guests. Children 6 and
 * under stay free, 7-12 pay half the adult rate (1,500 a night), 13 and over
 * pay the adult rate. Ananya's final answers, 2026-08-29.
 *
 * The age fields are EXCLUSIVE thresholds: `childFreeUnderAge` 7 means free
 * below 7, so six-year-olds are free. Guest-facing copy must not print those
 * numbers raw -- use ageBands() below, which converts them to the inclusive
 * ages people actually say ("6 and under", "7-12").
 *
 * NOTE: this runs in the browser, so the total it produces is not a trusted
 * figure. Bookings are confirmed by hand over WhatsApp before any money moves,
 * which is the compensating control. If a real payment gateway is ever added,
 * this calculation has to move behind a Lambda resolver first -- see the
 * price-is-server-derived gate in e2e/authz/GATES.md.
 */

/**
 * The thresholds as inclusive ages, for copy. `childFreeUnderAge: 7` is the
 * rule "free below 7"; nobody says that, they say "6 and under".
 */
export const ageBands = (pkg) => {
  const freeUnder = pkg?.childFreeUnderAge ?? 0;
  const childMax = pkg?.childMaxAge ?? 0;
  return {
    freeUpTo: freeUnder - 1,
    childFrom: freeUnder,
    childTo: childMax,
    adultFrom: childMax + 1,
    freeLabel: freeUnder > 0 ? `${freeUnder - 1} and under` : 'none',
    childLabel: `${freeUnder}–${childMax}`,
  };
};

export const formatINR = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

/** Nights between two YYYY-MM-DD dates, floor 1. */
export const nightsBetween = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;
  const ms = new Date(endDate).getTime() - new Date(startDate).getTime();
  return Math.max(0, Math.round(ms / 86400000));
};

/**
 * @param pkg       StayPackage with price, childPrice, childFreeUnderAge, childMaxAge
 * @param nights    number of nights
 * @param adults    count of adults
 * @param childAges array of ages, e.g. [4, 8]
 */
export const quoteStay = ({ pkg, nights, adults = 0, childAges = [] }) => {
  const rate = Number(pkg?.price) || 0;
  const childRate = Number(pkg?.childPrice) || 0;
  const freeUnder = pkg?.childFreeUnderAge ?? 0;
  const childMax = pkg?.childMaxAge ?? 0;

  // PER_PERSON_PER_NIGHT is the live package; the others exist so a future
  // package can be priced differently without another schema change.
  const unit = pkg?.priceUnit || 'PER_PERSON_PER_NIGHT';
  const nightsFactor = unit === 'PER_PERSON_PER_NIGHT' || unit === 'PER_NIGHT' ? (nights || 0) : 1;

  const lines = [];
  let total = 0;

  if (unit === 'PER_PACKAGE') {
    total = rate;
    lines.push({ label: 'Weekend package', qty: 1, amount: rate });
  } else {
    const adultAmount = rate * adults * nightsFactor;
    total += adultAmount;
    if (adults > 0) {
      lines.push({
        label: `${adults} × adult`,
        detail: nightsFactor > 1 ? `${formatINR(rate)} × ${nightsFactor} nights` : formatINR(rate),
        qty: adults,
        amount: adultAmount,
      });
    }
  }

  const free = childAges.filter((a) => a < freeUnder);
  const paid = childAges.filter((a) => a >= freeUnder && a <= childMax);
  const asAdult = childAges.filter((a) => a > childMax);

  if (paid.length) {
    const amount = childRate * paid.length * nightsFactor;
    total += amount;
    lines.push({
      label: `${paid.length} × child (${freeUnder}–${childMax})`,
      detail: nightsFactor > 1 ? `${formatINR(childRate)} × ${nightsFactor} nights` : formatINR(childRate),
      qty: paid.length,
      amount,
    });
  }
  if (asAdult.length) {
    const amount = rate * asAdult.length * nightsFactor;
    total += amount;
    lines.push({
      label: `${asAdult.length} × guest (over ${childMax})`,
      detail: 'charged at adult rate',
      qty: asAdult.length,
      amount,
    });
  }
  if (free.length) {
    lines.push({
      label: `${free.length} × child (${ageBands(pkg).freeLabel})`,
      qty: free.length, amount: 0, free: true,
    });
  }

  return { lines, total, freeChildren: free.length };
};

/** Guest count that counts toward min/max — free under-age children don't. */
export const chargeableGuests = (pkg, adults, childAges = []) =>
  adults + childAges.filter((a) => a >= (pkg?.childFreeUnderAge ?? 0)).length;

/**
 * Heads against the occupancy cap.
 *
 * Ananya's rule (answersheet, 2026-08-29): "Standard maximum occupancy is 4
 * guests, with an exception for up to 4 adults + 1 child under 6." So children
 * DO count -- but exactly one child under 6 is exempt, which is what makes the
 * 4+1 exception work while 4 adults + 2 toddlers stays over the line.
 *
 * This is not the same as `chargeableGuests`, which answers "who pays". Using
 * that for the cap let 4 adults plus any number of under-6s through, because
 * free children were simply not counted.
 */
export const countedGuests = (pkg, adults, childAges = []) => {
  const freeUnder = pkg?.childFreeUnderAge ?? 0;
  const exemptions = Math.min(1, childAges.filter((a) => a < freeUnder).length);
  return adults + childAges.length - exemptions;
};

export const validateParty = (pkg, adults, childAges = []) => {
  const min = pkg?.minGuests ?? 1;
  const max = pkg?.maxGuests ?? 99;
  const counted = countedGuests(pkg, adults, childAges);
  if (adults < 1) return 'At least one adult is needed.';
  if (chargeableGuests(pkg, adults, childAges) < min) return `This stay is for ${min} guests or more.`;
  if (counted > max) {
    // Name the exception, otherwise a family of four with a toddler reads the
    // cap as a flat "4" and gives up before asking.
    return `The farm takes ${max} guests — or ${max} adults with one child aged ${ageBands(pkg).freeLabel}.`;
  }
  return null;
};

/**
 * A day visit is its own product with its own rate card, not a stay of zero
 * nights, so it does not go through quoteStay. Per person, with the same age
 * bands as the stay: 6 and under free, 7-12 at half, 13 and over full.
 *
 * The half rate is derived from the tier rather than read from `childPrice` --
 * childPrice is the per-NIGHT figure and would be nonsense against a day rate.
 * Both come off the one policy ("children pay half"), which is why
 * childRatePercent exists rather than a second hardcoded number.
 */
export const quoteDayVisit = ({ tier, pkg, adults = 0, childAges = [] }) => {
  // The booking page quotes on every render, including before the guest has
  // picked a tier. Bail out rather than reading a label off null.
  if (!tier) return { lines: [], total: 0, freeChildren: 0 };
  const rate = Number(tier.price) || 0;
  const percent = pkg?.childRatePercent ?? 50;
  const freeUnder = pkg?.childFreeUnderAge ?? 0;
  const childMax = pkg?.childMaxAge ?? 0;
  const childRate = Math.round(rate * (percent / 100));
  const bands = ageBands(pkg);

  const free = childAges.filter((a) => a < freeUnder);
  const half = childAges.filter((a) => a >= freeUnder && a <= childMax);
  const full = adults + childAges.filter((a) => a > childMax).length;

  const lines = [];
  let total = 0;

  if (full > 0) {
    total += rate * full;
    lines.push({
      label: `${full} × ${tier.label}`,
      detail: `${formatINR(rate)} per person`,
      qty: full,
      amount: rate * full,
    });
  }
  if (half.length) {
    total += childRate * half.length;
    lines.push({
      label: `${half.length} × child (${bands.childLabel})`,
      detail: `${formatINR(childRate)} — ${percent}% of the adult rate`,
      qty: half.length,
      amount: childRate * half.length,
    });
  }
  if (free.length) {
    lines.push({
      label: `${free.length} × child (${bands.freeLabel})`,
      qty: free.length, amount: 0, free: true,
    });
  }

  return { lines, total, freeChildren: free.length };
};
